import React, { FC } from 'react';
import { Text } from '@fiverr-private/typography';
import { useIsClient } from '@fiverr-private/hooks';
import { Types } from '@fiverr-private/theme';
import { Center } from '@fiverr-private/layout_components';
import { Domingo } from '@fiverr-private/domingo';
import { getContext } from '@fiverr-private/fiverr_context';
import { AppI18n } from '../../../../../i18n';
import { usIsLg, usIsMd, usIsSm, usIsXl } from '../../../../../hooks/useMediaQuery';
import { buildRolesQueryParam, usePortfolioWidgetContext } from '../../../PortfolioWidgetContext';
import { MAX_VERTICAL_THUMBNAILS_AMOUNT, useLayoutContext } from '../Layout';
import { useBiClickOnSeeAll } from '../../../PortfolioWidget.bi';
import { Thumbnail, Variant } from './Thumbnail';
import { ThumbnailSkeleton } from './Thumbnail.Skeleton';

export interface ThumbnailLastProps {
  variant: Variant;
  amount?: number;
}

export const ThumbnailLast: FC<ThumbnailLastProps> = ({ variant, amount }) => {
  const { username, roleIds, roles, subCategoryId } = usePortfolioWidgetContext();

  const horizontalMore = useMore(amount);

  const isClient = useIsClient();

  const onClick = useBiClickOnSeeAll();

  if (!isClient || !amount) {
    return <ThumbnailSkeleton isLast variant={variant} />;
  }

  const more = variant === 'vertical' ? amount - MAX_VERTICAL_THUMBNAILS_AMOUNT : horizontalMore;

  if (more < 1) {
    return null;
  }

  const query = buildRolesQueryParam({ roleIds, roles, subCategoryId });

  return (
    <Thumbnail
      isLast
      variant={variant}
      onClick={onClick}
      href={Domingo(getContext().url).path('portfolio_listing_page', { username }, { query }).url}
    >
      <Center width="100%" height="100%" as="span" textAlign="center">
        <Text size="b_lg" as="span" fontWeight="bold">
          +{more}
        </Text>
        <Text size="b_md" as="span" color="bodySecondary">
          <AppI18n k="widget.projects" />
        </Text>
      </Center>
    </Thumbnail>
  );
};

const useMore = (amount?: number) => {
  const { thumbnailsAmount } = useLayoutContext();

  const isSm = usIsSm();
  const isMd = usIsMd();
  const isLg = usIsLg();
  const isXl = usIsXl();

  if (!amount) {
    return 0;
  }

  const sizedTAmount = amountMapper(thumbnailsAmount);

  if (isXl) {
    return amount - sizedTAmount.xl;
  }

  if (isLg) {
    return amount - sizedTAmount.lg;
  }

  if (isMd) {
    return amount - sizedTAmount.md;
  }

  if (isSm) {
    return amount - sizedTAmount.sm;
  }

  return amount - sizedTAmount.default;
};

export const amountMapper = (amount: Types.ResponsiveObject<number>) => {
  if (typeof amount === 'number') {
    return {
      default: amount,
      sm: amount,
      md: amount,
      lg: amount,
      xl: amount,
    };
  }

  const defaultAmount = amount.default as number;
  const smAmount = amount.sm || defaultAmount;
  const mdAmount = amount.md || smAmount;
  const lgAmount = amount.lg || mdAmount;
  const xlAmount = amount.xl || lgAmount;

  return {
    default: defaultAmount,
    sm: smAmount,
    md: mdAmount,
    lg: lgAmount,
    xl: xlAmount,
  };
};
